<template>
    <div v-show="isLoaded" class="content-wrapper">
        <div class="common-form-wrapper no-print">
            <div class="common-form">
                <div class="log-settings">
                    <div v-for="setting in settings" :key="setting.id">
                        <label>{{ setting.name }}</label>
                        <div class="chkbox">
                            <input
                                :id="setting.id"
                                v-model="setting.value"
                                type="checkbox"
                                :disabled="setting.disabled"
                            />
                            <label :for="setting.id"></label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapActions, mapState } from 'vuex';
import mappingState from '../common/mappingState';
import { SUCCESS } from '../../constants';
import { resourceLogSettings } from '../../api/resources';
import { getCancelToken } from '../../api';
import commonDirectives from '../common/commonDirectives';
import cloneDeep from 'lodash-es/cloneDeep';
import { ifIsNotCanceled } from '../common/helpers';

export default {
    mixins: [mappingState, commonDirectives],

    data() {
        return {
            cancelToken: getCancelToken(),
            settings: [],
            settingsCopy: [],
            isLoaded: false,
        };
    },

    computed: {
        ...mapState(['isLoading', 'unsavedChanges']),
    },

    watch: {
        settings: {
            handler() {
                this.setUnsavedChanges(true);
                if (!this.settings.find(l => l.name === 'Call logging').value) {
                    this.settings.find(l => l.name === 'Card logging').value = false;
                    this.settings.find(l => l.name === 'Card logging').disabled = true;
                    this.settings.find(l => l.name === 'Message logging').value = false;
                    this.settings.find(l => l.name === 'Message logging').disabled = true;
                } else {
                    this.settings.find(l => l.name === 'Card logging').disabled = false;
                    this.settings.find(l => l.name === 'Message logging').disabled = false;
                }
            },
            deep: true,
        },
    },

    created() {
        this.setSaveUndoButtons({
            undo: this.undo.bind(this),
            save: this.save.bind(this),
            saveDisabled: () => !this.unsavedChanges,
            wasChanged: () => !this.unsavedChanges,
        });
        this.loadLogSettings();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapActions({
            setLogSettings: 'setLogSettings',
        }),

        loadLogSettings() {
            this.showLoading(true);
            return resourceLogSettings({
                cancelToken: this.cancelToken.token,
            })
                .then(response => response.data)
                .then(info => {
                    this.settings = [
                        {
                            name: 'Call logging',
                            id: 'callLogging',
                            value: info.call,
                            disabled: false,
                        },
                        {
                            name: 'Card logging',
                            id: 'cardLogging',
                            value: info.card,
                            disabled: info.call,
                        },
                        {
                            name: 'Message logging',
                            id: 'messageLogging',
                            value: info.message,
                            disabled: info.call,
                        },
                    ];
                    this.settingsCopy = cloneDeep(this.settings);
                    this.setLogSettings(this.settingsCopy);
                })
                .catch(ifIsNotCanceled())
                .then(response => {
                    if (!response || (!!response && response !== 'canceled')) {
                        this.setUnsavedChanges(false);
                        this.showLoading(false);
                    }
                    this.isLoaded = true;
                });
        },
        /**
         * Sends log settings into database
         */
        save() {
            if (this.unsavedChanges) {
                this.showLoading(true);
                return resourceLogSettings({
                    method: 'post',
                    data: {
                        call: this.settings.find(l => l.name === 'Call logging').value,
                        card: this.settings.find(l => l.name === 'Card logging').value,
                        message: this.settings.find(l => l.name === 'Message logging').value,
                    },
                    cancelToken: this.cancelToken.token,
                })
                    .then(() => {
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                        this.setUnsavedChanges(false);
                        this.settingsCopy = cloneDeep(this.settings);
                        this.setLogSettings(this.settingsCopy);
                    })
                    .catch(ifIsNotCanceled())
                    .then(() => {
                        this.showLoading(false);
                    });
            }
        },

        /**
         * Reset organization data to the previous saved data
         */
        undo() {
            this.settings = cloneDeep(this.settingsCopy);
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },
    },
};
</script>
